import { ArrowRight, ExternalLink, X } from "lucide-react";
import { type CSSProperties, useEffect, useState } from "react";
import { Link } from "wouter";
import { rpcClient } from "@/lib/rpc";
import { useMarkdownHtml } from "@/pages/labs/_shared/use-markdown-html";
import type { ExtractedProfileViewModel } from "../results/extracted-profile-panel";
import { C, DM, UI } from "../results/ui-tokens";
import { initials, Muted } from "./ui";
import "./profile-drawer.css";

/** Only the slice of the get-evaluation payload this drawer needs. The endpoint
 *  also returns `result`/`modelId`/etc., but the résumé view is profile-only. */
interface ProfileEvaluation {
  evaluationId: string;
  candidateName: string;
  candidateSubtitle?: string;
  source: "linkedin" | "free-text";
  linkedinUrl: string | null;
  extractedProfile: ExtractedProfileViewModel;
}

type ChipTone = "muted" | "amber" | "forest";

/** Data-quality chips — the same completeness signal the standalone report's
 *  ExtractedProfilePanel surfaces, so "thin profile" reads consistently across
 *  surfaces. Lets the recruiter gauge how much the level rests on. */
function qualityChips(p: ExtractedProfileViewModel): { label: string; tone: ChipTone }[] {
  const { stats, source, narrative } = p;
  if (source === "linkedin") {
    return [
      {
        label: `${stats.positionsCount} posições`,
        tone: stats.positionsCount === 0 ? "amber" : "muted",
      },
      {
        label: `${stats.positionsWithDescription}/${stats.positionsCount} com descrição`,
        tone: stats.positionsCount > 0 && stats.positionsWithDescription === 0 ? "amber" : "muted",
      },
      {
        label: stats.hasSummary ? "Resumo presente" : "Sem resumo (about)",
        tone: stats.hasSummary ? "forest" : "amber",
      },
      {
        label: `${stats.educationsCount} formações · ${stats.skillsCount} skills`,
        tone: "muted",
      },
    ];
  }
  if (source === "free-text") {
    return [{ label: `${narrative.length.toLocaleString("pt-BR")} caracteres`, tone: "muted" }];
  }
  return [];
}

function chipStyle(tone: ChipTone): CSSProperties {
  const bg = tone === "amber" ? C.amberBg : tone === "forest" ? C.greenBg : C.ink5;
  const fg = tone === "amber" ? C.amberFg : tone === "forest" ? C.greenFg : C.ink50;
  return {
    ...UI,
    fontSize: 11,
    fontWeight: 600,
    padding: "3px 9px",
    borderRadius: 999,
    background: bg,
    color: fg,
  };
}

/**
 * Read-only résumé drawer for a set candidate. Renders the SAME extracted
 * profile narrative the scoring pipeline fed the model (so the recruiter sees
 * exactly the evidence behind the level), plus a data-quality summary and a
 * link to the live LinkedIn profile.
 *
 * Distinct from {@link CandidateDrawer}, which is the per-competency review
 * surface reached via the "Revisar" button. Clicking a candidate row opens
 * this; "Revisar" opens that. The "Revisar competências" CTA here bridges from
 * the résumé into the review flow.
 */
export function ProfileDrawer({
  evaluationId,
  onClose,
  onReview,
}: {
  evaluationId: string;
  onClose: () => void;
  /** Bridge into the per-competency review surface (the "Revisar" flow). */
  onReview: () => void;
}) {
  const [data, setData] = useState<ProfileEvaluation | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentEvaluationGet({
          evaluationId,
        })) as ProfileEvaluation;
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Erro inesperado");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [evaluationId]);

  const narrative = data?.extractedProfile?.narrative ?? "";
  const narrativeHtml = useMarkdownHtml(narrative);
  const chips = data ? qualityChips(data.extractedProfile) : [];

  return (
    <>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop pattern, redundant with the explicit close button inside */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss is mouse-only; the drawer has its own focusable close button */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(31,27,23,0.28)",
          zIndex: 40,
        }}
      />
      <aside
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 560,
          maxWidth: "100vw",
          background: C.white,
          boxShadow: "-12px 0 32px -10px rgba(0,0,0,0.22)",
          display: "flex",
          flexDirection: "column",
          zIndex: 41,
        }}
      >
        {/* Head */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            padding: "18px 22px",
            borderBottom: `1px solid ${C.borderSoft}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 13, minWidth: 0 }}>
            <div
              style={{
                width: 46,
                height: 46,
                flex: "none",
                borderRadius: 12,
                background: C.ink,
                color: C.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ...UI,
                fontWeight: 700,
                fontSize: 15,
              }}
            >
              {initials(data?.candidateName ?? "?")}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
              <span style={{ ...DM, fontWeight: 700, fontSize: 20, color: C.ink }}>
                {data?.candidateName ?? "Carregando…"}
              </span>
              {data?.candidateSubtitle && (
                <span style={{ ...UI, fontSize: 13, color: C.ink50 }}>
                  {data.candidateSubtitle}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{
              width: 32,
              height: 32,
              flex: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 999,
              border: 0,
              background: "transparent",
              color: C.ink50,
              cursor: "pointer",
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 22px" }}>
          {error && <Muted>Falha ao carregar o perfil: {error}</Muted>}
          {!data && !error && <Muted>Carregando perfil…</Muted>}

          {data && (
            <>
              {/* Actions: open the live profile + bridge into the review flow */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {data.linkedinUrl && (
                  <a
                    href={data.linkedinUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      ...UI,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontWeight: 600,
                      fontSize: 13,
                      color: C.ink70,
                      textDecoration: "none",
                      border: `1px solid ${C.border}`,
                      borderRadius: 999,
                      padding: "6px 13px",
                    }}
                  >
                    <ExternalLink size={15} />
                    Abrir no LinkedIn
                  </a>
                )}
                <div style={{ flex: 1 }} />
                <button
                  type="button"
                  onClick={onReview}
                  style={{
                    ...UI,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontWeight: 600,
                    fontSize: 13,
                    color: C.white,
                    background: C.red,
                    border: 0,
                    borderRadius: 999,
                    padding: "7px 15px",
                    cursor: "pointer",
                  }}
                >
                  Revisar competências
                </button>
              </div>

              {/* Profile completeness */}
              {chips.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 14 }}>
                  {chips.map((c) => (
                    <span key={c.label} style={chipStyle(c.tone)}>
                      {c.label}
                    </span>
                  ))}
                </div>
              )}

              {/* The extracted narrative — exactly what the model evaluated */}
              <div style={{ marginTop: 18 }}>
                {narrative ? (
                  <div
                    className="la-profile-resume"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: narrative is the stored extracted-profile markdown rendered through marked; same pattern as market-level-section.tsx
                    dangerouslySetInnerHTML={{ __html: narrativeHtml }}
                  />
                ) : (
                  <Muted>Nenhum dado de perfil foi extraído para este candidato.</Muted>
                )}
              </div>

              {/* Deep link to the full evaluation report */}
              <div
                style={{
                  marginTop: 22,
                  paddingTop: 16,
                  borderTop: `1px solid ${C.borderSoft}`,
                }}
              >
                <Link
                  href={`/labs/level-assessment/e/${evaluationId}`}
                  style={{
                    ...UI,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontWeight: 600,
                    fontSize: 13,
                    color: C.red,
                    textDecoration: "none",
                  }}
                >
                  Ver avaliação completa
                  <ArrowRight size={16} />
                </Link>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
